// src/components/dashboard/WeeklyHealthInsights.jsx
import React, {useEffect, useState, useCallback, useRef} from 'react';
import {
    Box,
    Heading,
    Text,
    Spinner,
    Button,
    useToast,
    List,
    ListItem,
    Code, Divider, useDisclosure, IconButton, Flex, Collapse
} from '@chakra-ui/react';
import { useAuth } from '../../../contexts/AuthContext';
import { getWeeklyInsights } from '../../../data/WeeklyInsights';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {ChevronDownIcon, ChevronUpIcon} from "@chakra-ui/icons";

export default function WeeklyInsights() {
    const { user } = useAuth();
    const toast = useToast();
    const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });


    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const hasFetchedRef = useRef(false);

    // Try to load existing summary text
    const fetchStored = useCallback(async () => {
        if (!user) return false;
        try {
            const {data, error} = await getWeeklyInsights(user.id);
            if (error) throw new Error(error);
            if (data) {
                setData({
                    text: data.text,
                    generated_at: data.generated_at
                });
                return true;
            }
        } catch (err) {
            console.warn('No stored weekly insight or error:', err);
        }
        return false;
    }, [user]);

    // Generate a new weekly summary text
    const generate = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        setError(null);
        try {
            const res = await fetch('/api/generateWeeklySummary', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ profileId: user.id })
            });
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || res.statusText);
            }
            const json = await res.json();
            setData(json);
        } catch (err) {
            console.error(err);
            setError(err);
            toast({
                title: 'Error generating weekly summary',
                description: err.message,
                status: 'error',
                duration: 5000,
                isClosable: true
            });
        } finally {
            setLoading(false);
        }
    }, [user, toast]);

    // On mount: try fetch from DB, else generate
    useEffect(() => {
        if (!user || hasFetchedRef.current) return;
        hasFetchedRef.current = true;
        (async () => {
            const exists = await fetchStored();
            if (!exists) generate();
        })();
    }, [user, fetchStored, generate]);

    const mdComponents = {
        h3: (props) => <Heading as="h3" size="md" mt={4} mb={2} {...props} />,
        p:  (props) => <Text mb={2} {...props} />,
        ul: (props) => <List styleType="disc" pl={6} mb={2} {...props} />,
        ol: (props) => <List as="ol" styleType="decimal" pl={6} mb={2} {...props} />,
        li: (props) => <ListItem mb={1} {...props} />,
        code: (props) => <Code p={1} rounded="md" {...props} />,
        hr: () => <Divider my={4} />

    };

    if (loading) {
        return (
            <Box textAlign="center" p={6}>
                <Spinner size="lg" />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={6} bg="red.50" rounded="md">
                <Text color="red.600">Failed to load insights: {error.message}</Text>
                <Button mt={4} size="sm" onClick={generate}>Retry</Button>
            </Box>
        );
    }

    // No text yet
    if (!data || !data.text) {
        return (
            <Box p={6} bg="white" rounded="lg" shadow="sm">
                <Heading size="md" mb={2}>Weekly Health Insights</Heading>
                <Text color="gray.600" mb={4}>
                    We’ll generate your first weekly summary once you’ve logged a full week of meals, weights, and goals.
                </Text>
                <Button size="sm" onClick={generate}>Generate Now</Button>
            </Box>
        );
    }

    // Render markdown text
    return (
        <Box p={6} bg="white" rounded="lg" shadow="sm">
            <Flex align="center" justify="space-between" mb={2}>
                <Heading size="md">Weekly Health Insights</Heading>
                <IconButton
                    size="sm"
                    aria-label={isOpen ? 'Collapse' : 'Expand'}
                    icon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    onClick={onToggle}
                    variant="ghost"
                />
            </Flex>
            <Collapse in={isOpen} animateOpacity>
                <Box
                    p={6}
                    bg="white"
                    rounded="lg"
                    shadow="sm"
                >
                    <Divider mb={4} />
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={mdComponents}
                    >
                        {data.text}
                    </ReactMarkdown>
                    <Text fontSize="sm" color="gray.500" mt={2}>
                        Updated {data.generated_at ? new Date(data.generated_at).toDateString() : new Date().toDateString()}
                    </Text>
                </Box>
            </Collapse>
        </Box>
    );
}